/**
 * <s-hero-reveal>: sunrise hero. Scroll progress drives --sun (sky warmth), --rise with
 * --sun-x/--sun-y/--sun-vis (the sun arcs fast out past the top-right corner and fades) and
 * --light (the shaft from that corner, growing with the unlocks and peaking at 3/3); the shaft tilts
 * toward the hovered card
 * (--ray-rot). On top of that,
 * the featured [data-reveal-card] demos (a straight row of up to 5, centre first, then outward in pairs; on lg each is
 * born from its own constellation: --cd draw, --cg gather, --sa bloom, set on the li) are "unlocked" on
 * scroll. Each card gets --a (arrives from depth) then --v (frosted veil clears
 * from the centre); [data-unlocked] plays a one-off light sweep and enables the
 * Theme Store CTA. [data-unlock-count] shows how many are revealed. Hovered cards
 * lean toward the pointer with a glare (fine pointers only).
 * On load the copy is centred on screen (--shift is measured, --m glides it up).
 * lg+: driven by progress through the tall sticky scene; smaller screens: by each
 * card's own position. No JS or reduced motion: everything stays revealed.
 */
class SHeroReveal extends HTMLElement {
  #ticking = false;
  #io = null;
  #ro = null;
  #frame = 0;
  #aimed = null;
  #centred = false;
  #centre = -1;
  #flowAt = 0;
  #flowTimer = 0;
  #sunX = 108;
  #sunY = -12;
  #wide = window.matchMedia('(min-width: 64rem)');
  #onResize = () => {
    this.#measure();
    this.#onScroll();
  };
  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => this.#update());
  };
  #onMove = (event) => {
    if (event.pointerType !== 'mouse' || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      // Hit-test on the untransformed list item, not the tilted card: near a corner the tilt
      // pulled the card out from under the pointer, so hover flickered on and off.
      const item = event.target.closest?.('[data-hero-cards] li');
      const card = item?.querySelector('[data-reveal-card][data-unlocked]');
      if (!card) return;
      if (!card.hasAttribute('data-hover')) {
        card.setAttribute('data-hover', '');
        this.#focus(item);
      }
      const box = item.getBoundingClientRect();
      const px = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
      const py = Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);
      card.style.setProperty('--ry', `${((px - 0.5) * 12).toFixed(2)}deg`);
      card.style.setProperty('--rx', `${((0.5 - py) * 9).toFixed(2)}deg`);
      card.style.setProperty('--gx', `${(px * 100).toFixed(1)}%`);
      card.style.setProperty('--gy', `${(py * 100).toFixed(1)}%`);
      this.#aim(card);
    });
  };
  #onOut = (event) => {
    const item = event.target.closest?.('[data-hero-cards] li');
    if (!item || item.contains(event.relatedTarget)) return;
    const card = item.querySelector('[data-reveal-card]');
    if (!card) return;
    card.removeAttribute('data-hover');
    this.#focus(null);
    card.style.removeProperty('--rx');
    card.style.removeProperty('--ry');
    this.style.removeProperty('--ray-rot');
    this.#aimed = null;
  };

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.cards = [...this.querySelectorAll('[data-reveal-card]')];
    this.counter = this.querySelector('[data-unlock-count]');
    this.grid = this.querySelector('[data-hero-cards]');
    this.setAttribute('data-live', '');
    this.#measure();
    this.#update();
    if (this.grid) {
      this.#ro = new ResizeObserver(() => { this.#measure(); this.#update(); });
      this.#ro.observe(this.grid);
    }
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('pointermove', this.#onMove, { passive: true });
      this.addEventListener('pointerout', this.#onOut);
    }
    this.#io = new IntersectionObserver(([entry]) => this.toggleAttribute('data-paused', !entry.isIntersecting), { rootMargin: '120px' });
    this.#io.observe(this);
  }


  /**
   * lg: cover flow on the horizon (merchant 2026-10-29). The centre demo is the big one, facing front; the others
   * shrink, turn inward in 3D, dim and drop along the horizon curve. Hovering a side demo (160ms intent, 650ms
   * settle lock so the row never chases the pointer) slides it into the centre; the row keeps that centre when
   * the pointer leaves. Below lg: the hovered card simply comes forward (swipe row).
   */
  #focus(item) {
    const list = this.querySelector('[data-hero-fan]');
    if (!list) return;
    const items = [...list.children];
    if (this.#wide.matches && this.hasAttribute('data-flow')) {
      clearTimeout(this.#flowTimer);
      list.removeAttribute('data-focus');
      if (!item) {
        items.forEach((li) => li.removeAttribute('data-front'));
        return;
      }
      const index = items.indexOf(item);
      if (index === this.#centre) {
        item.setAttribute('data-front', '');
        return;
      }
      this.#flowTimer = setTimeout(() => {
        if (performance.now() - this.#flowAt < 650) return;
        this.#flow(index);
        items.forEach((li, i) => li.toggleAttribute('data-front', i === index && li.matches(':hover')));
      }, 160);
      return;
    }
    const index = item ? items.indexOf(item) : -1;
    items.forEach((li, i) => {
      li.toggleAttribute('data-front', i === index);
      li.style.setProperty('--push', index < 0 ? '0' : String(Math.sign(i - index)));
    });
    list.toggleAttribute('data-focus', index >= 0);
  }

  /** Places every demo around the centre one (index; -1 = the featured centre demo). Writes --cf-* on each li. */
  #flow(index) {
    const list = this.querySelector('[data-hero-fan]');
    if (!list) return;
    const items = [...list.children];
    if (index < 0 || index >= items.length) index = Math.max(items.findIndex((li) => li.dataset.fanStep === '0'), 0);
    if (index !== this.#centre) this.#flowAt = performance.now();
    this.#centre = index;
    this.setAttribute('data-flow', '');
    const w = parseFloat(this.style.getPropertyValue('--card-w')) || 280;
    const spread = [0, 0.66, 1.16, 1.5];
    const drop = [0, 0.05, 0.15, 0.25];
    const size = [1, 0.8, 0.64, 0.5];
    const light = [1, 0.72, 0.5, 0.4];
    items.forEach((li, i) => {
      const p = i - index;
      const a = Math.min(Math.abs(p), 3);
      const s = Math.sign(p);
      li.style.setProperty('--cf-x', `${(s * spread[a] * w).toFixed(1)}px`);
      li.style.setProperty('--cf-y', `${(drop[a] * w).toFixed(1)}px`);
      li.style.setProperty('--cf-r', `${(-s * Math.min(a, 1) * 32).toFixed(1)}deg`);
      li.style.setProperty('--cf-t', `${(s * a * 1.5).toFixed(1)}deg`);
      li.style.setProperty('--cf-s', String(size[a]));
      li.style.setProperty('--cf-b', String(light[a]));
      li.style.setProperty('--cf-z', String(10 - a));
      li.toggleAttribute('data-centre', a === 0);
    });
  }

  /** Tilts the light shaft around the sun so it lands on the hovered card; only when the hovered card changes. */
  #aim(card) {
    if (card === this.#aimed) return;
    this.#aimed = card;
    const scene = this.querySelector('[data-hero-scene]')?.getBoundingClientRect();
    if (!scene || !this.grid) return;
    const origin = { x: scene.left + (scene.width * this.#sunX) / 100, y: scene.top + (scene.height * this.#sunY) / 100 };
    const angle = (box) => Math.atan2(box.top + box.height / 2 - origin.y, box.left + box.width / 2 - origin.x) * (180 / Math.PI);
    const rot = angle(card.getBoundingClientRect()) - angle(this.grid.getBoundingClientRect());
    this.style.setProperty('--ray-rot', `${rot.toFixed(2)}deg`);
  }
  #measure() {
    if (!this.grid) return;
    const list = this.grid.querySelector('ul');
    const wrap = this.grid.parentElement;
    const copy = this.querySelector('[data-hero-copy-wrap]');
    if (this.#wide.matches && list && wrap && copy) {
      // lg: content is top-aligned. On load the copy is centred (--shift); as the demos arrive the
      // rating chip and the stats row fade out and give their height (--c1, --c2) to the cards,
      // which are sized from the real space left on screen (tallest demo wins, nothing is cropped).
      const wrapStyle = getComputedStyle(wrap);
      const wrapGap = parseFloat(wrapStyle.rowGap) || 0;
      const chrome = parseFloat(wrapStyle.paddingTop) + parseFloat(wrapStyle.paddingBottom);
      const listGap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const copyEl = this.querySelector('[data-hero-copy]');
      const copyGap = copyEl ? parseFloat(getComputedStyle(copyEl).rowGap) || 0 : 0;
      const chip = this.querySelector('[data-hero-collapse="chip"]');
      const trust = this.querySelector('[data-hero-collapse="trust"]');
      const c1 = chip ? chip.offsetHeight + copyGap : 0;
      // Everything under the title that folds away (subtitle, CTA row, stats) gives its height to the demos.
      const c2 = [...this.querySelectorAll('[data-hero-collapse]:not([data-hero-collapse="chip"])')].reduce((sum, el) => sum + el.offsetHeight + copyGap, 0);
      this.style.setProperty('--c1', `${c1.toFixed(1)}px`);
      this.style.setProperty('--c2', `${c2.toFixed(1)}px`);
      let ratio = 0.7;
      let caption = 0;
      this.cards.forEach((card) => {
        const frame = card.querySelector('[data-card-frame]');
        if (!frame || !frame.offsetWidth) return;
        ratio = Math.max(ratio, frame.offsetHeight / frame.offsetWidth);
        caption = Math.max(caption, card.offsetHeight - frame.offsetHeight);
      });
      const vh = window.innerHeight;
      const other = copy.offsetHeight - c1 - c2 + wrapGap + (this.grid.offsetHeight - list.offsetHeight) + chrome;
      const room = Math.max(vh - other - 12, 160);
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // Straight row of n cards with a small gap (stars become themes): the row is n × 1.06 card widths, all
      // the same size, so any card sets the height; nothing is cropped.
      const n = Math.max(this.cards.length, 1);
      // Cover flow: the side demos are scaled down, so the centre one may use a little more than the free height
      // (merchant 2026-10-29: too small on a 13" laptop); the outer cards stay well inside it.
      const fromHeight = (room - caption) / ratio;
      // Cover flow: the centre demo plus two steps each side spans about 3 card widths.
      const fromWidth = this.grid.clientWidth / Math.min(n * 1.06, 3);
      const cardW = Math.max(12 * remPx, Math.min(fromHeight, fromWidth, 27 * remPx));
      this.style.setProperty('--card-w', `${Math.round(cardW)}px`);
      this.style.setProperty('--cards-max', `${Math.round(cardW * n * 1.06)}px`);
      // On tall screens the capped row leaves spare height: centre the final layout in it.
      const cardH = cardW * ratio + caption;
      this.style.setProperty('--center', `${Math.max(0, (room - cardH) / 2).toFixed(1)}px`);
      this.style.setProperty('--cf-h', `${Math.round(cardH)}px`);
      this.#flow(this.#centre);
      this.style.setProperty('--shift', `${((vh - copy.offsetHeight) / 2 - copy.offsetTop).toFixed(1)}px`);
      return;
    }
    this.style.removeProperty('--cards-max');
    this.style.removeProperty('--card-w');
    // Small screens: the fan is a swipe row; start on the centre (featured) card once.
    if (list && !this.#centred) {
      const middle = list.querySelector('[data-fan-step="0"]');
      if (middle && list.scrollWidth > list.clientWidth) {
        list.scrollLeft = middle.offsetLeft - (list.clientWidth - middle.offsetWidth) / 2;
        this.#centred = true;
      }
    }
    const gap = parseFloat(getComputedStyle(wrap).rowGap) || 0;
    this.style.setProperty('--shift', `${((this.grid.offsetHeight + gap) / 2).toFixed(1)}px`);
  }

  disconnectedCallback() {
    document.documentElement.style.removeProperty("--hero-light");
    document.documentElement.style.removeProperty("--hero-sun");
    this.#ro?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerout', this.#onOut);
    this.#io?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#flowTimer);
  }

  // Perf (2026-10-29 audit): each style write re-styles the hero, and the two on the root re-style the whole
  // page, so values are written only when they change and nothing runs while the position is unchanged
  // (e.g. scrolling far below the hero).
  #vals = new WeakMap();
  #lastKey = '';
  #set(el, prop, value) {
    let seen = this.#vals.get(el);
    if (!seen) { seen = {}; this.#vals.set(el, seen); }
    if (seen[prop] === value) return;
    seen[prop] = value;
    el.style.setProperty(prop, value);
  }

  #update() {
    this.#ticking = false;
    const clamp = (value) => Math.min(Math.max(value, 0), 1);
    const smooth = (t) => t * t * (3 - 2 * t);
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const p = clamp(-rect.top / Math.max(rect.height - vh, 1));
    const wide = this.#wide.matches;
    // Small screens read each card's position, so they always run; lg depends on p only.
    const key = `${p.toFixed(4)}|${vh}|${wide}`;
    if (wide && key === this.#lastKey) return;
    this.#lastKey = key;
    if (!wide && rect.bottom < -vh) return;
    this.#set(this, '--p', p.toFixed(4));
    this.#set(this, '--m', smooth(clamp(p / 0.22)).toFixed(4));
    // Folded-away copy (subtitle, CTA row, stats) must not stay clickable while invisible.
    this.toggleAttribute('data-folded', smooth(clamp(p / 0.22)) > 0.5);
    this.#set(this, '--sun', smooth(clamp(p / 0.88)).toFixed(4));
    // The sun is only seen while scrolling: it rises fast on an arc out past the top-right corner and
    // is gone before the first demo shows; a faint light shaft then falls from that corner.
    const rise = smooth(clamp((p - 0.02) / 0.28));
    this.#sunX = 50 + 58 * rise * rise;
    this.#sunY = 66 - 78 * (1 - (1 - rise) * (1 - rise));
    const sunVis = clamp(p / 0.04) * (1 - smooth(clamp((rise - 0.7) / 0.3)));
    this.#set(this, '--sun-vis', sunVis.toFixed(4));
    this.#set(this, '--rise', rise.toFixed(4));
    this.#set(this, '--sun-x', this.#sunX.toFixed(2) + '%');
    this.#set(this, '--sun-y', this.#sunY.toFixed(2) + '%');
    // Light grows with the unlocks: from the first demo arriving to all three open (p ≈ 0.88).
    const light = smooth(clamp((p - 0.14) / 0.72));
    this.#set(this, '--light', light.toFixed(4));
    // Invisible layers stop animating (CSS): the sun group once it has set, the light shaft before dawn.
    this.toggleAttribute('data-sun-gone', sunVis <= 0.001);
    this.toggleAttribute('data-unlit', light <= 0.001);
    // The header logo (outside this element) brightens with the same dawn.
    this.#set(document.documentElement, '--hero-light', light.toFixed(4));
    this.#set(document.documentElement, '--hero-sun', smooth(clamp(p / 0.88)).toFixed(4));
    let unlocked = 0;
    this.cards.forEach((card) => {
      let r;
      if (wide) {
        // Centre card first, then the pair beside it, then the outer pair (3 beats, like the light).
        const step = Number(card.closest('li')?.dataset.fanStep) || 0;
        r = clamp((p - 0.12 - step * 0.2) / 0.36);
      } else {
        const box = card.getBoundingClientRect();
        // Merchant 2026-11-03: hidden at the top of the page, revealed only by scrolling (not by load position).
        r = Math.min(clamp((vh - box.top) / (vh * 0.8)), clamp(window.scrollY / (vh * 0.35)));
      }
      // lg: the constellation draws (cd), gathers into its bright centre star (cg), then the demo blooms
      // from that point (arrive) and its veil clears. Small screens keep the simple rise.
      const arrive = wide ? smooth(clamp((r - 0.26) / 0.4)) : smooth(clamp(r / 0.55));
      const veil = wide ? smooth(clamp((r - 0.52) / 0.44)) : smooth(clamp((r - 0.45) / 0.55));
      const li = card.closest('li');
      if (li && wide) {
        this.#set(li, '--cd', smooth(clamp(r / 0.18)).toFixed(4));
        this.#set(li, '--cg', smooth(clamp((r - 0.15) / 0.13)).toFixed(4));
        this.#set(li, '--sa', arrive.toFixed(4));
      }
      this.#set(card, '--a', arrive.toFixed(4));
      this.#set(card, '--v', veil.toFixed(4));
      const open = veil > 0.92;
      if (open) unlocked += 1;
      if (card.hasAttribute('data-unlocked') !== open) card.toggleAttribute('data-unlocked', open);
      if (!open && card.hasAttribute('data-hover')) {
        card.removeAttribute('data-hover');
        if (card.closest('li')?.hasAttribute('data-front')) this.#focus(null);
      }
    });
    if (this.counter && this.counter.textContent !== String(unlocked)) this.counter.textContent = String(unlocked);
  }
}

if (!customElements.get('s-hero-reveal')) customElements.define('s-hero-reveal', SHeroReveal);
